import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Link
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// The app's established "a clinician has looked at this and stands behind it"
// glyph — the same icon used for a clinically confirmed diagnosis. Shared
// deliberately: the claim is of the same kind, so the symbol should be too.
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import IconButton from '@mui/material/IconButton';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import {
  paleChipSx,
  severityFromInterpretation
} from 'src/components/UI/chips/paleChipSx';
import {
  variantSources,
  hasClassificationConflict,
  discrepancyReview
} from 'src/util/variantPathogenicity';
import DiscrepancyReviewDialog from './DiscrepancyReviewDialog';
import { VARIANT_INTERPRETATION_OPTIONS } from '../clinical-notes-section/forms/formService';
import { TILE_COLOURS } from '../tileColours';

const HGVS_FIELDS = [
  { key: 'variantG', prefix: 'g.' },
  { key: 'variantC', prefix: 'c.' },
  { key: 'variantP', prefix: 'p.' }
];

const renderHgvs = (variant) => {
  const parts = HGVS_FIELDS.map(({ key, prefix }) => {
    const raw = variant[key];
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
  }).filter(Boolean);

  if (parts.length === 0) return variant.gene || 'Unspecified variant';
  return parts.join(' · ');
};

// One tooltip, three states. The unresolved and stale cases must not read the
// same: a stale review means someone DID look, and what they looked at has
// since moved.
const discrepancyTooltip = (review, dateFormat) => {
  if (review.reviewed) {
    const who = review.current?.reviewedBy;
    const when = review.current?.reviewedAt
      ? moment(review.current.reviewedAt).format(dateFormat)
      : null;
    return `Laboratory and ClinVar classifications differ. Reviewed${
      who ? ` by ${who}` : ''
    }${when ? ` on ${when}` : ''}. Click to view or review again.`;
  }
  if (review.stale) {
    return 'Laboratory and ClinVar classifications differ. A previous review no longer applies because a source or the determination has changed since. Click to review again.';
  }
  return 'Laboratory and ClinVar classifications differ, and this has not been reviewed. Click to review.';
};

const VariantRow = ({
  variant,
  onClinicianInterpretationChange,
  onReviewDiscrepancy,
  reviewerName,
  onSourceTestClick,
  disabled
}) => {
  const { theme } = useContext(ThemeContext);
  const [reviewOpen, setReviewOpen] = useState(false);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  // One shared predicate across every surface (pedigree marker, legend, this
  // row) instead of the three subtly different rules that used to disagree —
  // a record could warn here and not on the chart. Bucketed, so Pathogenic vs
  // Likely pathogenic is concordant and only a material disagreement warns.
  const showDiscordance = hasClassificationConflict(variant);
  // A discrepancy that has been explicitly reviewed is a different state from
  // an unresolved one, and from one whose review no longer applies because a
  // source has since changed its mind.
  const review = discrepancyReview(variant);

  const sources = variantSources(variant);
  const labSource = sources.find((s) => s.key === 'lab');
  const clinvarSource = sources.find((s) => s.key === 'clinvar');
  const clinvar = clinvarSource?.provenance || {};

  const sourceLabel = [
    variant.sourceTestName,
    variant.sourceTestDate
      ? moment(variant.sourceTestDate).format(dateFormat)
      : ''
  ]
    .filter(Boolean)
    .join(' – ');

  return (
    <Box
      sx={{
        border: `1px solid ${TILE_COLOURS.genotype.border}`,
        borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
        backgroundColor: TILE_COLOURS.genotype.background,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 1
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: TILE_COLOURS.genotype.text,
          lineHeight: 1
        }}
      >
        Variant
      </Typography>

      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: 13,
            wordBreak: 'break-word'
          }}
        >
          {renderHgvs(variant)}
        </Typography>
        <VariantOriginChip origin={variant.origin} />
      </Box>

      {sourceLabel && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
        >
          From:{' '}
          {variant.sourceTestId ? (
            <Link
              component="button"
              type="button"
              underline="always"
              variant="caption"
              onClick={() => {
                const testId = String(variant.sourceTestId);
                if (onSourceTestClick) {
                  onSourceTestClick({ testId });
                  return;
                }
                window.dispatchEvent(
                  new CustomEvent('open-source-test-record', {
                    detail: { testId }
                  })
                );
              }}
              sx={{ verticalAlign: 'baseline', fontWeight: 500 }}
            >
              {sourceLabel}
            </Link>
          ) : (
            sourceLabel
          )}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        {/* Each source with its own classification, always present. A
            laboratory that made no call is stated explicitly — that absence is
            clinically meaningful, and hiding the chip is what allowed a
            ClinVar-only assertion to read as the record's own verdict. */}
        <Chip
          size="small"
          variant={labSource?.classification ? 'filled' : 'outlined'}
          label={
            labSource?.classification
              ? `Laboratory: ${labSource.classification}`
              : 'Laboratory: no classification reported'
          }
          sx={paleChipSx(
            severityFromInterpretation(labSource?.classification || '')
          )}
        />

        <ClinvarChip source={clinvarSource} provenance={clinvar} />

        <ClinicianSelect
          value={variant.clinicianInterpretation || ''}
          disabled={disabled}
          onChange={(value) =>
            onClinicianInterpretationChange(variant.variantId, value)
          }
        />

        {showDiscordance && (
          <Tooltip title={discrepancyTooltip(review, dateFormat)}>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setReviewOpen(true)}
              aria-label={
                review.reviewed
                  ? 'View clinician review of this discrepancy'
                  : 'Review this classification discrepancy'
              }
              sx={{ p: 0.25 }}
            >
              {review.reviewed ? (
                <CheckCircleOutlinedIcon
                  sx={{ fontSize: 18, color: 'green' }}
                />
              ) : (
                <WarningAmberIcon color="warning" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Summary only. The icon above is the control — a text link here was
          too easy to miss for something safety-relevant, and after review it
          offered no way back into what had been decided. */}
      {showDiscordance && (
        <Box sx={{ mt: 0.5 }}>
          {review.reviewed ? (
            <Typography sx={{ fontSize: 11.5, opacity: 0.8 }}>
              Clinician-reviewed
              {review.current?.reviewedBy
                ? ` by ${review.current.reviewedBy}`
                : ''}
              {review.current?.reviewedAt
                ? ` on ${moment(review.current.reviewedAt).format(dateFormat)}`
                : ''}
              {review.history.length > 1
                ? ` · ${review.history.length} reviews`
                : ''}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 11.5, color: 'warning.main' }}>
              {review.stale
                ? 'A source or the determination has changed since this was reviewed — needs reviewing again'
                : 'Laboratory and ClinVar do not agree — not yet reviewed'}
            </Typography>
          )}
        </Box>
      )}

      <DiscrepancyReviewDialog
        open={reviewOpen}
        variant={variant}
        reviewerName={reviewerName}
        onCancel={() => setReviewOpen(false)}
        dateFormat={dateFormat}
        onConfirm={({ determination, note }) => {
          setReviewOpen(false);
          if (onReviewDiscrepancy)
            onReviewDiscrepancy(variant.variantId, { determination, note });
        }}
      />

      <ClinvarProvenance provenance={clinvar} />
    </Box>
  );
};

// Inherited vs acquired.
//
// Only rendered when the origin has actually been STATED. An unstated origin
// deliberately shows nothing rather than "germline": the analyzer treats it as
// potentially heritable, and labelling it germline on screen would assert
// something no one recorded.
//
// Somatic is the clinically notable case — it means the variant was acquired
// and cannot have been transmitted, so it is not evidence of an inherited
// cause however pathogenic it is.
const VariantOriginChip = ({ origin }) => {
  const value = String(origin || '').toLowerCase();
  if (value !== 'somatic' && value !== 'germline') return null;
  const somatic = value === 'somatic';
  return (
    <Tooltip
      title={
        somatic
          ? 'Recorded as somatic: acquired rather than inherited, so it is not evidence of a transmitted cause.'
          : 'Recorded as germline: potentially transmitted to relatives.'
      }
    >
      <Chip
        size="small"
        variant="outlined"
        label={somatic ? 'Somatic' : 'Germline'}
        sx={{
          height: 18,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.04em'
        }}
      />
    </Tooltip>
  );
};

// ClinVar's star rating. Rendered only when we actually know it — an unknown
// rating must never render as zero stars, which would read as a real
// assessment of low confidence.
const ReviewStars = ({ stars }) => {
  if (!Number.isFinite(stars)) return null;
  return (
    <Box component="span" sx={{ letterSpacing: 1 }}>
      {'★'.repeat(stars)}
      {'☆'.repeat(Math.max(0, 4 - stars))}
    </Box>
  );
};

// Why ClinVar has no classification for this variant. These are different
// clinical statements and must not look alike: an outage is not an absence of
// evidence, and an ambiguous match is not a negative result.
const LOOKUP_STATE_LABEL = {
  lookup_error: 'ClinVar: lookup unavailable',
  ambiguous: 'ClinVar: several records match — none applied',
  insufficient_identifiers: 'ClinVar: needs HGVS notation to match',
  no_match: 'ClinVar: no matching record',
  not_attempted: 'ClinVar: not checked',
  vocabulary_unavailable: 'ClinVar: reference data unavailable'
};

const LOOKUP_STATE_TOOLTIP = {
  lookup_error:
    'The ClinVar lookup did not complete. This is not a statement about the variant — it will be retried.',
  ambiguous:
    'More than one ClinVar record matches this notation, so none was applied. Resolve by recording a more specific HGVS notation.',
  insufficient_identifiers:
    'A gene symbol alone cannot identify a variant. Record a c., g. or p. notation to enable a ClinVar match.',
  no_match: 'No ClinVar record matches this variant.',
  not_attempted: 'No ClinVar lookup has been attempted for this variant yet.',
  // Distinct from no_match on purpose: the lookup ran and answered honestly,
  // but out of an empty ClinVar vocabulary, so its answer says nothing about
  // this variant (TGCLOUD-1785).
  vocabulary_unavailable:
    'The ClinVar reference data is not loaded, so this variant could not be checked. This is not a statement about the variant. Please contact an admin.'
};

const ClinvarChip = ({ source, provenance }) => {
  const classification = source?.classification;

  if (!classification) {
    const status = provenance?.lookupStatus || 'not_attempted';
    const label =
      LOOKUP_STATE_LABEL[status] || LOOKUP_STATE_LABEL.not_attempted;
    return (
      <Tooltip
        title={
          LOOKUP_STATE_TOOLTIP[status] || LOOKUP_STATE_TOOLTIP.not_attempted
        }
      >
        <Chip
          size="small"
          variant="outlined"
          label={label}
          sx={paleChipSx(
            status === 'lookup_error' || status === 'vocabulary_unavailable'
              ? 'uncertain'
              : 'unclassified'
          )}
        />
      </Tooltip>
    );
  }

  const detail = [
    Number.isFinite(provenance.submitterCount)
      ? `${provenance.submitterCount} submitter${
          provenance.submitterCount === 1 ? '' : 's'
        }`
      : null,
    provenance.reviewStatus,
    provenance.lastEvaluated
      ? `last evaluated ${provenance.lastEvaluated}`
      : null
  ].filter(Boolean);

  const chip = (
    <Chip
      size="small"
      // The source's own words, verbatim — including compound classifications
      // such as "Pathogenic/Likely pathogenic" and ClinVar's own
      // "Conflicting classifications of pathogenicity", which used to be
      // dropped entirely because they matched no canonical tier.
      label={`ClinVar: ${classification}`}
      sx={[
        paleChipSx(severityFromInterpretation(classification)),
        provenance.variationId ? { cursor: 'pointer' } : null
      ]}
    />
  );

  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
          <Box>
            <strong>ClinVar:</strong> {classification}
          </Box>
          {provenance.reviewStars !== null && (
            <Box>
              <strong>Review status:</strong>{' '}
              <ReviewStars stars={provenance.reviewStars} />
            </Box>
          )}
          {detail.map((d) => (
            <Box key={d}>{d}</Box>
          ))}
          {/* Expert-panel and practice-guideline assertions are labelled
              distinctly: they carry recognised evidentiary weight that a raw
              classification string does not convey. */}
          {provenance.practiceGuideline && (
            <Box sx={{ mt: 0.5 }}>
              <strong>Practice guideline assertion</strong>
            </Box>
          )}
          {provenance.expertPanel && (
            <Box sx={{ mt: 0.5 }}>
              <strong>Reviewed by expert panel</strong>
            </Box>
          )}
          {provenance.internalConflict && (
            <Box sx={{ mt: 0.5 }}>
              ClinVar reports conflicting submissions for this variant.
            </Box>
          )}
          {provenance.variationId && (
            <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>
              Click to open in ClinVar
            </Box>
          )}
        </Box>
      }
    >
      {provenance.variationId ? (
        <a
          href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${provenance.variationId}/`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'inline-flex' }}
        >
          {chip}
        </a>
      ) : (
        chip
      )}
    </Tooltip>
  );
};

// When the displayed value was retrieved, and from which dataset. Without
// this, what a clinician saw on a given date cannot be reconstructed after the
// fact.
const ClinvarProvenance = ({ provenance }) => {
  const parts = [
    provenance?.retrievedAt
      ? `Retrieved ${moment(provenance.retrievedAt).format('D MMM YYYY')}`
      : null,
    provenance?.datasetVersion ? `ClinVar ${provenance.datasetVersion}` : null
  ].filter(Boolean);

  if (parts.length === 0 && !provenance?.researchUseOnly) return null;

  return (
    <Typography sx={{ mt: 0.5, fontSize: 10.5, opacity: 0.65 }}>
      {parts.join(' · ')}
      {provenance?.researchUseOnly && (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {parts.length > 0 ? ' · ' : ''}Research use only
        </Box>
      )}
    </Typography>
  );
};

const ClinicianSelect = ({ value, onChange, disabled }) => {
  const severity = severityFromInterpretation(value);

  return (
    <FormControl size="small" disabled={disabled}>
      <Select
        displayEmpty
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) => `Clinician: ${v || 'None'}`}
        sx={(theme) => ({
          height: 24,
          fontSize: 12,
          ...paleChipSx(severity)(theme),
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': {
            py: 0,
            pl: 1,
            pr: '24px !important',
            display: 'flex',
            alignItems: 'center'
          }
        })}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {VARIANT_INTERPRETATION_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default VariantRow;
